import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Leaf, Mail, Lock, User, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import NativeAuthButtons from '../components/NativeAuthButtons';
import { isNativePlatform } from '../lib/native';
import WebProviderButtons from '../components/WebProviderButtons';
import { isWebGoogleEnabled } from '../lib/webIdentity';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await register(email, password, name);
      toast.success('Account created! Let\'s set up your journey.');
      navigate('/onboarding');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#1C1C1E] flex flex-col">
      {/* Subtle glow */}
      <div className="fixed top-1/3 left-1/2 -translate-x-1/2 w-[400px] h-[400px] bg-[#8BAA9E]/5 rounded-full blur-[100px] pointer-events-none" />
      
      <div className="flex-1 flex flex-col justify-center px-6 py-12">
        <div className="max-w-sm mx-auto w-full">
          {/* Logo */}
          <Link to="/" className="flex items-center justify-center gap-3 mb-12">
            <div className="w-12 h-12 rounded-full bg-[#8BAA9E]/20 flex items-center justify-center">
              <Leaf className="w-6 h-6 text-[#8BAA9E]" strokeWidth={1.5} />
            </div>
            <span className="font-serif text-2xl text-[#DCD3CA]">FutureSelf</span>
          </Link>

          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="font-serif text-3xl text-[#DCD3CA] mb-3">Begin Your Journey</h1>
            <p className="text-[#B5B1AB] font-sans font-light">Create your account to start transforming</p>
          </div>

          {/* Native buttons (renders nothing on web). New Apple/Google
              accounts get created via /api/auth/{apple,google/native} and
              redirected to onboarding. */}
          <NativeAuthButtons redirectTo="/onboarding" />
          <WebProviderButtons redirectTo="/onboarding" />

          {/* Google Signup — WEB ONLY. */}
          {!isNativePlatform() && !isWebGoogleEnabled() && (
          <Button
            variant="outline"
            className="w-full border-[#B8AFA6]/30 text-[#DCD3CA] hover:bg-[#B8AFA6]/10 rounded-2xl py-6 mb-6 flex items-center justify-center gap-3"
            onClick={handleGoogleLogin}
            data-testid="google-signup-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#8BAA9E" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#B8AFA6" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#C6A75E" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#A8A8A8" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign up with Google
          </Button>
          )}

          {/* Divider */}
          <div className={`flex items-center gap-4 mb-6 ${isNativePlatform() ? 'hidden' : ''}`}>
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-[#7A7670] text-sm font-sans">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Register Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="relative">
              <User className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-[#7A7670]" strokeWidth={1.5} />
              <Input
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-transparent border-0 border-b border-white/10 rounded-none pl-8 py-4 text-[#DCD3CA] placeholder-[#7A7670] focus:border-[#8BAA9E] focus:ring-0"
                data-testid="name-input"
              />
            </div>

            <div className="relative">
              <Mail className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-[#7A7670]" strokeWidth={1.5} />
              <Input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent border-0 border-b border-white/10 rounded-none pl-8 py-4 text-[#DCD3CA] placeholder-[#7A7670] focus:border-[#8BAA9E] focus:ring-0"
                data-testid="email-input"
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-[#7A7670]" strokeWidth={1.5} />
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Create password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-transparent border-0 border-b border-white/10 rounded-none pl-8 pr-10 py-4 text-[#DCD3CA] placeholder-[#7A7670] focus:border-[#8BAA9E] focus:ring-0"
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-0 top-1/2 -translate-y-1/2 text-[#7A7670] hover:text-[#B5B1AB]"
              >
                {showPassword ? <EyeOff className="w-5 h-5" strokeWidth={1.5} /> : <Eye className="w-5 h-5" strokeWidth={1.5} />}
              </button>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#8BAA9E] hover:bg-[#A3C2B6] text-[#1C1C1E] rounded-full py-6 text-base font-medium mt-8 flex items-center justify-center gap-2"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-[#1C1C1E] border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Create Account
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </form>

          {/* Login Link */}
          <p className="text-center mt-8 text-[#B5B1AB] font-sans">
            Already have an account?{' '}
            <Link to="/login" className="text-[#8BAA9E] hover:text-[#A3C2B6]" data-testid="login-link">
              Sign in
            </Link>
          </p>

          {/* Terms — links open the public legal pages; safe while
              logged out (they are outside ProtectedRoute in App.js). */}
          <p className="text-center mt-6 text-[#7A7670] text-xs font-sans">
            By creating an account, you agree to our{' '}
            <Link
              to="/terms"
              className="text-[#8BAA9E] hover:text-[#A3C2B6] underline"
              data-testid="register-terms-link"
            >
              Terms of Use
            </Link>{' '}
            and{' '}
            <Link
              to="/privacy"
              className="text-[#8BAA9E] hover:text-[#A3C2B6] underline"
              data-testid="register-privacy-link"
            >
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
